import React, { useState } from 'react';
import { Building2, Calculator, DollarSign, Calendar, Percent } from 'lucide-react';

interface BankRate {
  bank: string;
  amount: string;
  term: string;
  rate: string;
  annualRate: string;
}

const bankRates: BankRate[] = [
  { bank: 'Akbank', amount: '1.000-500.000 ₺', term: '3-36 Ay', rate: '%5,35', annualRate: '%125,40 - %132,16' },
  { bank: 'Alternatif Bank', amount: '1.000-250.000 ₺', term: '3-36 Ay', rate: '%4,19 - %4,49', annualRate: '%90,25 - %99,90' },
  { bank: 'Burgan Bank', amount: '1.000-70.000 ₺', term: '3-36 Ay', rate: '%0,00 - %3,39', annualRate: '%0,00 - %67,79' },
  { bank: 'Denizbank', amount: '1.000-250.000 ₺', term: '3-36 Ay', rate: '%4,42 - %4,72', annualRate: '%97,70 - %105,80' },
  { bank: 'Enpara.com', amount: '1.000-250.000 ₺', term: '3-36 Ay', rate: '%0,00 - %5,05', annualRate: '%0,00 - %114,47' },
  { bank: 'Fibabanka', amount: '1.000-250.000 ₺', term: '3-36 Ay', rate: '%5,54', annualRate: '%131,75 - %138,68' },
  { bank: 'Garanti BBVA', amount: '1.000-300.000 ₺', term: '3-36 Ay', rate: '%4,48 - %4,79', annualRate: '%98,80 - %108,94' },
  { bank: 'Halk Bankası', amount: '1.000-150.000 ₺', term: '3-36 Ay', rate: '%5,24', annualRate: '%121,80 - %128,46' },
  { bank: 'Hayat Finans', amount: '500-200.000 ₺', term: '3-18 Ay', rate: '%3,99 - %4,25', annualRate: '%83,46 - %90,66' },
  { bank: 'HSBC', amount: '1.000-99.999 ₺', term: '3-36 Ay', rate: '%4,30 - %4,48', annualRate: '%93,67 - %98,96' },
  { bank: 'ICBC', amount: '1.000-100.000 ₺', term: '3-36 Ay', rate: '%4,99', annualRate: '%113,81 - %120,25' },
  { bank: 'ING', amount: '0-10.000.000 ₺', term: '3-36 Ay', rate: '%0,99 - %4,99', annualRate: '%0,00 - %115,17' },
  { bank: 'İş Bankası', amount: '1.000-3.000.000 ₺', term: '3-36 Ay', rate: '%4,14', annualRate: '%88,58 - %94,31' },
  { bank: 'Odea', amount: '1.000-10.000 ₺', term: '3-36 Ay', rate: '%6,99', annualRate: '%185,88 - %194,28' },
  { bank: 'QNB Finansbank', amount: '1.000-250.000 ₺', term: '3-36 Ay', rate: '%0,00 - %4,54', annualRate: '%3,52 - %101,38' },
  { bank: 'Şekerbank', amount: '1.000-50.000 ₺', term: '3-36 Ay', rate: '%4,72', annualRate: '%105,47 - %111,69' },
  { bank: 'TEB', amount: '1.000-200.000 ₺', term: '3-36 Ay', rate: '%4,39 - %4,59', annualRate: '%93,25 - %98,73' },
  { bank: 'VakıfBank', amount: '3.000-125.000 ₺', term: '3-36 Ay', rate: '%5,00', annualRate: '%114,12 - %120,57' },
  { bank: 'Yapı Kredi', amount: '1.000-999.999 ₺', term: '3-36 Ay', rate: '%5,50 - %5,80', annualRate: '%124,17 - %130,27' },
  { bank: 'Ziraat Bankası', amount: '1.000-100.000 ₺', term: '3-36 Ay', rate: '%4,94', annualRate: '%112,24 - %118,64' }
];

type LoanType = 'ihtiyac' | 'konut' | 'tasit';

const BankInterestRates = () => {
  const [selectedType, setSelectedType] = useState<LoanType>('ihtiyac');

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="p-6 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
        <div className="flex items-center gap-3 mb-3">
          <Calculator className="h-6 w-6 text-[#F7BA45]" />
          <h2 className="text-xl font-bold">Bankaların Faiz Oranları</h2>
        </div>
        <p className="text-slate-300 text-sm">
          Güncel kredi faiz oranlarını karşılaştırın, size en uygun krediyi seçin
        </p>
      </div>

      {/* Loan Type Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex">
          <button
            onClick={() => setSelectedType('ihtiyac')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative
              ${selectedType === 'ihtiyac' 
                ? 'text-[#F7BA45] border-b-2 border-[#F7BA45]' 
                : 'text-slate-600 hover:text-slate-900'}`}
          >
            İhtiyaç Kredisi
          </button>
          <button
            onClick={() => setSelectedType('konut')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative
              ${selectedType === 'konut' 
                ? 'text-[#F7BA45] border-b-2 border-[#F7BA45]' 
                : 'text-slate-600 hover:text-slate-900'}`}
          >
            Konut Kredisi
          </button>
          <button
            onClick={() => setSelectedType('tasit')}
            className={`px-6 py-3 text-sm font-medium transition-colors relative
              ${selectedType === 'tasit' 
                ? 'text-[#F7BA45] border-b-2 border-[#F7BA45]' 
                : 'text-slate-600 hover:text-slate-900'}`}
          >
            Taşıt Kredisi
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#F7BA45]" />
                  Bankalar
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-[#F7BA45]" />
                  Kredi Tutarı
                  <span className="text-xs text-slate-500">(min-maks)</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#F7BA45]" />
                  Kredi Vadesi
                  <span className="text-xs text-slate-500">(min-maks)</span>
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <Percent className="h-4 w-4 text-[#F7BA45]" />
                  Faiz/Kar Payı
                </div>
              </th>
              <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-[#F7BA45]" />
                  Yıllık Maliyet Oranı
                  <span className="text-xs text-slate-500">(min-maks)</span>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {bankRates.map((bank, index) => (
              <tr 
                key={index}
                className="hover:bg-slate-50 transition-colors"
              >
                <td className="px-6 py-4 text-sm font-medium text-slate-900">{bank.bank}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{bank.amount}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{bank.term}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{bank.rate}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{bank.annualRate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Disclaimer */}
      <div className="p-4 bg-slate-50 border-t border-slate-200">
        <p className="text-xs text-slate-500 text-center">
          * Faiz oranları bankaların resmi web sitelerinden alınmıştır ve bilgilendirme amaçlıdır. 
          Kredinin toplam maliyeti; kredi tutarı, vade ve bankanın sunduğu özel faiz oranına göre değişiklik gösterebilir.
        </p>
      </div>
    </div>
  );
};

export default BankInterestRates;